import { create } from 'zustand';
import {
  emptyShift,
  type Shift,
} from '@/models/shift';
import {
  getCurrentShift,
  startNewShift as fbStartNewShift,
  writeShiftTotals,
  applyShiftTotalDeltaReadModifyWrite,
} from '@/services/firestore/shifts';
import { parseAmountValue } from '@/lib/amount';
import { fetchHourRate } from '@/services/api/hour-rate';
import { useAuthStore } from '@/stores/auth';

type ShiftState = {
  currentShift: Shift;
  cachedShiftTotal: number;
  cachedShiftRawTotal: number;
  // Hourly stipend (forfait horaire) for the current shift. Tracked separately
  // from cachedShiftTotal: it lives only in memory, comes from POST /hourRate,
  // and is added to cachedShiftTotal for display. Mirrors the Flutter
  // ShiftHourController.hoursBilled flow.
  cachedHoursBilled: number;
  hasActiveShift: boolean;
  verifyCurrentShift: (uid: string) => Promise<void>;
  applyKnownCurrentShift: (shift: Shift) => void;
  startNewShift: (args: {
    uid: string;
    mainPlace: string;
    mdType: string;
    facturationType: string;
  }) => Promise<void>;
  resetShift: () => void;
  refreshHoursBilled: () => Promise<void>;
  // applyShiftTotalDelta is the perf-critical write path. Acts/visits call it
  // on every add/edit/delete. It uses the in-memory cache as the source of
  // truth and writes the new totals directly to Firestore (no read).
  applyShiftTotalDelta: (
    uid: string,
    deltaBilled: number,
    deltaRawBilled: number,
  ) => Promise<void>;
};

export const useShiftStore = create<ShiftState>((set, get) => ({
  currentShift: emptyShift(),
  cachedShiftTotal: 0,
  cachedShiftRawTotal: 0,
  cachedHoursBilled: 0,
  hasActiveShift: false,

  verifyCurrentShift: async (uid) => {
    const shift = await getCurrentShift(uid);
    set({
      currentShift: shift,
      cachedShiftTotal: parseAmountValue(shift.totalBilled),
      cachedShiftRawTotal: parseAmountValue(shift.totalRawBilled),
      hasActiveShift: !!shift.dateStarted,
    });
    if (shift.dateStarted) void get().refreshHoursBilled();
    else set({ cachedHoursBilled: 0 });
  },

  applyKnownCurrentShift: (shift) => {
    set({
      currentShift: shift,
      cachedShiftTotal: parseAmountValue(shift.totalBilled),
      cachedShiftRawTotal: parseAmountValue(shift.totalRawBilled),
      hasActiveShift: true,
    });
    void get().refreshHoursBilled();
  },

  startNewShift: async (args) => {
    const shift = await fbStartNewShift(args);
    if (shift) get().applyKnownCurrentShift(shift);
    else await get().verifyCurrentShift(args.uid);
  },

  resetShift: () => {
    set({
      currentShift: emptyShift(),
      cachedShiftTotal: 0,
      cachedShiftRawTotal: 0,
      cachedHoursBilled: 0,
      hasActiveShift: false,
    });
  },

  refreshHoursBilled: async () => {
    const { currentShift } = get();
    if (!currentShift.dateStarted) {
      set({ cachedHoursBilled: 0 });
      return;
    }
    const profile = useAuthStore.getState().profile;
    if (!profile) return;
    try {
      const raw = await fetchHourRate({
        shiftStartIso: currentShift.dateStarted,
        mdType: profile.mdType || 'cfpc',
        facturationType: profile.billingType || 'mixte',
      });
      const value = Number.parseFloat(raw);
      set({ cachedHoursBilled: Number.isFinite(value) ? Math.round(value) : 0 });
    } catch {
      // Hourly rate is best-effort — never block UI on a failure.
    }
  },

  applyShiftTotalDelta: async (uid, deltaBilled, deltaRawBilled) => {
    if (deltaBilled === 0 && deltaRawBilled === 0) return;
    const { currentShift, cachedShiftTotal, cachedShiftRawTotal } = get();
    if (!currentShift.id) {
      const res = await applyShiftTotalDeltaReadModifyWrite({
        uid,
        deltaBilled,
        deltaRawBilled,
      });
      if (res) {
        set({
          cachedShiftTotal: res.newTotal,
          cachedShiftRawTotal: res.newRawTotal,
        });
      }
      return;
    }
    const newTotal = cachedShiftTotal + deltaBilled;
    const newRawTotal = cachedShiftRawTotal + deltaRawBilled;
    await writeShiftTotals({ uid, newTotal, newRawTotal });
    set({ cachedShiftTotal: newTotal, cachedShiftRawTotal: newRawTotal });
  },
}));
